import OpenReplay from "@openreplay/tracker";
import trackerRedux from "@openreplay/tracker-redux";
import { Middleware } from "@reduxjs/toolkit";

import { ENVIRONMENT } from "@/config";

const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY;
const ingestPoint = import.meta.env.VITE_OPENREPLAY_INGEST_POINT;
const isEnabled =
    import.meta.env.VITE_OPENREPLAY_ENABLED === "true" &&
    ENVIRONMENT !== "dev-local";

const ignoredActions = new Set(["offline/setLatency"]);

type RecordingReason = "error" | "task" | null;

type SavedRecordingState =
    | {
          reason: "error";
          stopAt: number;
      }
    | {
          reason: "task";
      };

class OpenReplayService {
    private userId: string | null = null;
    private metadata: Record<string, string> | null = null;

    private tracker: OpenReplay | null = null;
    private coldStarted = false;
    private recording = false;
    private starting = false;
    private initializing = false;
    private _reduxMiddleware: Middleware | null = null;
    private stopTimer: ReturnType<typeof setTimeout> | null = null;

    private static RECORDING_STATE_KEY = "or_recording_state";

    private recordingReason: RecordingReason = null;

    constructor() {
        if (!isEnabled || !projectKey) return;

        this.tracker = new OpenReplay({
            projectKey,
            ...(ingestPoint ? { ingestPoint } : {}),
            __DISABLE_SECURE_MODE: true,
            defaultInputMode: 0,
            obscureInputEmails: true,
            obscureTextEmails: true,
            network: {
                sessionTokenHeader: false,
                capturePayload: true,
                failuresOnly: false,
                ignoreHeaders: ["Api-Hub-Token", "Accesstoken"],
                captureInIframes: false,
                sanitizer: (data) => {
                    if (data.url?.includes("/ping")) {
                        return null;
                    }

                    const sensitiveMethods = [
                        "user.refresh",
                        "user.getOTP",
                        "user.login",
                    ];

                    try {
                        const body = JSON.parse(data.request.body ?? "{}");
                        if (sensitiveMethods.includes(body.method)) {
                            return null;
                        }
                    } catch {
                        // not JSON
                    }

                    return data;
                },
            },
        });

        this._reduxMiddleware = this.tracker.use(
            trackerRedux({
                actionTransformer: (action) => {
                    if (!action?.type) return action;

                    if (action.type.startsWith("persist/")) {
                        return { type: action.type };
                    }

                    if (ignoredActions.has(action.type)) {
                        return null;
                    }

                    return action;
                },
                stateTransformer: (state) => ({
                    ...state,
                    auth: "[hidden]",
                }),
            }),
        );
    }

    private getSavedRecordingState(): SavedRecordingState | null {
        const raw = sessionStorage.getItem(
            OpenReplayService.RECORDING_STATE_KEY,
        );

        if (!raw) return null;

        try {
            const parsed = JSON.parse(raw) as SavedRecordingState;

            if (parsed.reason === "task") {
                return parsed;
            }

            if (
                parsed.reason === "error" &&
                typeof parsed.stopAt === "number"
            ) {
                return parsed;
            }

            return null;
        } catch {
            return null;
        }
    }

    private saveRecordingState(state: SavedRecordingState) {
        sessionStorage.setItem(
            OpenReplayService.RECORDING_STATE_KEY,
            JSON.stringify(state),
        );
    }

    private clearRecordingState() {
        sessionStorage.removeItem(OpenReplayService.RECORDING_STATE_KEY);
    }

    private clearStopTimer() {
        if (this.stopTimer) {
            clearTimeout(this.stopTimer);
            this.stopTimer = null;
        }
    }

    private async activateRecording() {
        if (this.recording || this.starting) return;

        this.starting = true;

        try {
            await this.tracker.start();

            this.recording = true;

            this.identifyPerson(this.userId, this.metadata);
        } finally {
            this.starting = false;
        }
    }

    private resetRecording() {
        this.clearStopTimer();
        this.recording = false;
        this.recordingReason = null;
        this.clearRecordingState();
    }

    private stopRecording() {
        this.tracker?.stop();
        this.resetRecording();
    }

    private scheduleStop(ms: number) {
        this.clearStopTimer();

        const stopAt = Date.now() + ms;

        this.saveRecordingState({
            reason: "error",
            stopAt,
        });

        this.stopTimer = setTimeout(() => {
            if (this.recordingReason === "task") {
                return;
            }

            this.stopRecording();
        }, ms);
    }

    get instance() {
        return this.tracker;
    }

    get reduxMiddleware(): Middleware {
        if (!this._reduxMiddleware) {
            return () => (next) => (action) => next(action);
        }

        return this._reduxMiddleware;
    }

    isColdStarted() {
        return this.coldStarted;
    }

    isRecording() {
        return this.recording;
    }

    isInitializing() {
        return this.initializing;
    }

    getRecordingReason() {
        return this.recordingReason;
    }

    async coldStart(userId?: string, metadata?: Record<string, string>) {
        if (!this.tracker || this.coldStarted || this.initializing) {
            return false;
        }

        this.initializing = true;

        this.userId = userId ?? null;
        this.metadata = metadata ?? null;

        try {
            await this.tracker.coldStart({
                ...(userId ? { userID: userId } : {}),
                ...(metadata ? { metadata } : {}),
            });

            this.coldStarted = true;

            const savedState = this.getSavedRecordingState();

            if (savedState?.reason === "task") {
                await this.activateRecording();
                this.recordingReason = "task";
            }

            if (savedState?.reason === "error") {
                if (savedState.stopAt > Date.now()) {
                    await this.activateRecording();
                    this.recordingReason = "error";
                    this.scheduleStop(savedState.stopAt - Date.now());
                } else {
                    this.clearRecordingState();
                }
            }

            return true;
        } catch (error) {
            console.error("OpenReplay coldStart failed:", error);
            this.coldStarted = false;
            return false;
        } finally {
            this.initializing = false;
        }
    }

    async startOnError(eventName?: string, payload?: Record<string, string>) {
        if (!this.tracker || !this.coldStarted) return;

        try {
            await this.activateRecording();

            if (this.recordingReason !== "task") {
                this.recordingReason = "error";
                this.scheduleStop(60_000);
            }
        } catch (error) {
            console.error("OpenReplay startOnError failed:", error);
        } finally {
            if (eventName) {
                this.event(eventName, payload);
            }
        }
    }

    async startTaskRecording(taskId: string) {
        if (!this.tracker || !this.coldStarted) return false;

        try {
            await this.activateRecording();

            this.clearStopTimer();

            this.recordingReason = "task";
            this.saveRecordingState({
                reason: "task",
            });

            this.event("task_recording_started", {
                taskId,
            });

            return true;
        } catch (error) {
            console.error("OpenReplay startTaskRecording failed:", error);
            return false;
        }
    }

    stopTaskRecording(taskId: string, payload: unknown) {
        if (!this.tracker || !this.coldStarted || !this.recording) return;
        if (this.recordingReason !== "task") return;

        this.event("task_recording_stopped", {
            taskId,
            payload: String(payload),
        });

        this.stopRecording();
    }

    stop() {
        this.stopRecording();
    }

    setUser(userId: string) {
        if (!this.coldStarted) return;
        this.userId = userId;
        this.tracker?.setUserID(userId);
    }

    setMetadata(key: string, value: string) {
        if (!this.coldStarted) return;
        if (this.metadata) this.metadata[key] = value;
        this.tracker?.setMetadata(key, value);
    }

    identifyPerson(
        userId: string | null,
        properties?: Record<string, string> | null,
    ) {
        if (
            !this.coldStarted ||
            !this.recording ||
            !this.tracker?.analytics ||
            !userId
        ) {
            return;
        }

        this.tracker.analytics.identify(userId);

        if (properties) {
            this.tracker.analytics.people.setProperties(properties);
        }
    }

    event(name: string, payload?: Record<string, string>) {
        if (!this.coldStarted) return;
        this.tracker?.event(name, payload);
    }
}

export const openReplayService = new OpenReplayService();
